import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X, BellRing, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { api, isValidEmail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const DISMISS_KEY = "wa_exit_intent_dismissed_at";
const SUPPRESS_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const SHOWN_KEY = "wa_exit_intent_shown_this_session";

// Never trigger on these paths.
const BLOCK_PATHS = [
  "/login", "/register", "/wachtwoord", "/welkom", "/billing",
  "/admin", "/instellingen", "/uitschrijven", "/reset",
];

export const ExitIntentPopup = () => {
  const { user } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const armed = useRef(false);
  const readyAt = useRef(0);

  const isFreeUser = user && (user.subscription_tier || "free").toLowerCase() !== "premium";
  const mode = user ? (isFreeUser ? "premium" : "none") : "capture";

  useEffect(() => {
    // Reset dismissed state on route change so it can re-check
    setOpen(false);
    setErr("");
    setDone(false);
  }, [loc.pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (mode === "none") return;
    if (BLOCK_PATHS.some((p) => loc.pathname.startsWith(p))) return;

    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissed && Date.now() - dismissed < SUPPRESS_MS) return;
    if (sessionStorage.getItem(SHOWN_KEY) === "1") return;

    // Arm after 8s so we don't trigger on quick bounces
    const armTimer = setTimeout(() => { armed.current = true; readyAt.current = Date.now(); }, 8000);

    const onMouseLeave = (e) => {
      if (!armed.current) return;
      if (e.clientY <= 0 || e.relatedTarget === null) {
        show();
      }
    };
    const onVisibility = () => {
      if (!armed.current) return;
      if (document.visibilityState === "hidden") show();
    };
    // Mobile fallback: rapid scroll up near top of page after being armed
    let lastScrollY = window.scrollY;
    const onScroll = () => {
      if (!armed.current) return;
      const y = window.scrollY;
      const goingUpFast = lastScrollY - y > 40;
      lastScrollY = y;
      if (goingUpFast && y < 80) show();
    };
    const show = () => {
      if (sessionStorage.getItem(SHOWN_KEY) === "1") return;
      sessionStorage.setItem(SHOWN_KEY, "1");
      armed.current = false;
      setOpen(true);
    };

    document.addEventListener("mouseleave", onMouseLeave);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseleave", onMouseLeave);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("scroll", onScroll);
    };
  }, [mode, loc.pathname]);

  const close = (persistent = false) => {
    if (persistent) localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setErr("");
    if (!isValidEmail(email)) {
      setErr("Vul een geldig e-mailadres in.");
      return;
    }
    setBusy(true);
    try {
      await api("/waitlist", {
        method: "POST",
        body: JSON.stringify({ email, source: "exit_intent" }),
      });
      // Best-effort analytics
      try {
        await api("/analytics/track", {
          method: "POST",
          body: JSON.stringify({
            event_type: "action",
            event_name: "exit_intent_email_capture",
            screen: loc.pathname,
            props: { path: loc.pathname },
          }),
        });
      } catch { /* ignore */ }
      setDone(true);
      setTimeout(() => {
        close(true);
        navigate(`/register?email=${encodeURIComponent(email)}`);
      }, 1400);
    } catch (er) {
      setErr(er.message || "Er ging iets mis. Probeer nogmaals.");
    } finally {
      setBusy(false);
    }
  };

  if (!open || mode === "none") return null;

  return (
    <div
      data-testid="exit-intent-popup"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy/60 px-4 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={() => close(false)}
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          data-testid="exit-close"
          aria-label="Sluiten"
          onClick={() => close(true)}
          className="absolute right-4 top-4 z-10 rounded-full bg-white/90 p-1.5 text-slate-500 shadow-sm hover:bg-white hover:text-navy"
        >
          <X size={16} />
        </button>

        {mode === "capture" ? (
          <CaptureVariant
            email={email}
            setEmail={setEmail}
            submit={submitEmail}
            busy={busy}
            err={err}
            done={done}
          />
        ) : (
          <PremiumVariant close={() => close(true)} />
        )}
      </div>
    </div>
  );
};

const CaptureVariant = ({ email, setEmail, submit, busy, err, done }) => (
  <div className="p-8">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brandblue-light text-brandblue">
      <BellRing size={26} />
    </div>
    <h2 className="mt-5 font-display text-2xl font-extrabold text-navy">
      Wacht! Mis nooit meer een woning
    </h2>
    <p className="mt-2 text-sm text-slate-500">
      Zet gratis alerts aan en ontvang direct nieuwe woningen die bij jou passen.
      Ruim 89 huurders gingen je voor.
    </p>
    <form onSubmit={submit} className="mt-5 space-y-3">
      <Input
        type="email"
        placeholder="je@email.nl"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        data-testid="exit-email"
        className="rounded-full h-12 text-base"
        autoFocus
      />
      {err && <p className="text-xs text-danger" data-testid="exit-error">{err}</p>}
      <Button
        type="submit"
        data-testid="exit-submit"
        disabled={busy || done}
        className="w-full h-12 rounded-full bg-brandblue font-bold text-white hover:bg-brandblue/90"
      >
        {done ? "Bijna klaar…" : busy ? <Loader2 className="animate-spin" size={18} /> : (
          <>Zet gratis alerts aan <ArrowRight size={16} className="ml-2" /></>
        )}
      </Button>
    </form>
    <p className="mt-3 text-[11px] leading-tight text-slate-400">
      Gratis · direct opzegbaar · geen creditcard nodig.
    </p>
  </div>
);

const PremiumVariant = ({ close }) => (
  <div className="p-8">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-600">
      <Sparkles size={26} />
    </div>
    <h2 className="mt-5 font-display text-2xl font-extrabold text-navy">
      Nog even voor je gaat…
    </h2>
    <p className="mt-2 text-sm text-slate-500">
      Premium-users vinden gemiddeld <strong>3× sneller</strong> een woning. Twee tools maken het verschil:
    </p>
    <ul className="mt-4 space-y-2 text-sm text-slate-700">
      <li className="flex items-start gap-2">
        <Sparkles size={16} className="mt-0.5 shrink-0 text-amber-600" />
        <span><strong>Reactiecoach</strong> — sterke persoonlijke reactiebrief in seconden.</span>
      </li>
      <li className="flex items-start gap-2">
        <Sparkles size={16} className="mt-0.5 shrink-0 text-amber-600" />
        <span><strong>Foto-analyse</strong> — vocht, schimmel of achterstallig onderhoud vóór de bezichtiging.</span>
      </li>
    </ul>
    <Link
      to="/premium?utm_source=web&utm_medium=exit_intent&utm_campaign=upgrade"
      data-testid="exit-premium-cta"
      onClick={close}
      className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-3 text-base font-bold text-amber-300 hover:bg-navy/90"
    >
      Bekijk Premium <ArrowRight size={16} />
    </Link>
    <button
      type="button"
      data-testid="exit-premium-skip"
      onClick={close}
      className="mt-2 w-full rounded-full px-6 py-2.5 text-xs font-semibold text-slate-400 hover:text-slate-600"
    >
      Misschien later
    </button>
  </div>
);
